import { getPagePath, openPage } from "@nanostores/router";
import { ReactElement, useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormSetError,
} from "react-hook-form";
import { Panel, PanelGroup } from "react-resizable-panels";
import { toast } from "react-toastify";

import {
  ApiParameterType,
  createQueryAction,
  Integration,
  QueryAction,
  testQueryAction,
  updateQueryAction,
} from "../api";
import APIParameterInlineTable from "../components/APIParameterInlineTable.tsx";
import BreadCrumbs from "../components/BreadCrumbs.tsx";
import { CardLayout } from "../components/CardLayout.tsx";
import { Badge } from "../components/catalyst/Badge.tsx";
import { Button } from "../components/catalyst/Button.tsx";
import { ErrorMessage } from "../components/catalyst/ErrorMessage.tsx";
import { Field } from "../components/catalyst/Field.tsx";
import { Label } from "../components/catalyst/Label.tsx";
import { Switch } from "../components/catalyst/Switch.tsx";
import { SwitchField } from "../components/catalyst/SwitchField.tsx";
import EntitySelectField from "../components/inputs/EntitySelectField.tsx";
import FormErrorList from "../components/inputs/FormErrorList.tsx";
import InputFieldGroup from "../components/inputs/InputFieldGroup.tsx";
import SelectFieldGroup from "../components/inputs/SelectFieldGroup.tsx";
import MonacoJsonEditor from "../components/json/MonacoJsonEditor.tsx";
import MultiEntityMappingTable from "../components/MultiEntityMappingTable.tsx";
import PlaceholderDialog from "../components/PlaceholderDialog.tsx";
import { QueryActionAISettings } from "../components/QueryActionAISettings.tsx";
import { QueryActionPayloadField } from "../components/QueryActionPayloadField.tsx";
import { QueryActionURLField } from "../components/QueryActionURLField.tsx";
import SingleEntityMappingTable from "../components/SingleEntityMappingTable.tsx";
import { TabPanel, TabView } from "../components/TabView.tsx";
import { useServerErrors } from "../hooks/useServerErrors.ts";
import { $router } from "../stores/router.ts";
import { QueryActionFormInputs } from "../types/form-inputs.ts";
import { IntegrationType } from "../types/integration_definitions.tsx";
import { validateRequiredFields } from "../utils/form-validation.ts";
import {
  defaultEntitySchema,
  validateEntitySchema,
  wrapJsonLists,
} from "../utils/json-validation.ts";
import { entityPlaceholderRegex } from "../utils/regex-definitions.ts";

export type QueryActionFormProps = {
  integration: Pick<Integration, "baseUrl" | "id" | "name" | "type">;
  queryAction?: QueryAction;
};

// Map server field names to form field names for QueryActionForm
const fieldNameMapping: Record<string, keyof QueryActionFormInputs> = {
  "defaultAgenticSettings/confidenceLevel": "confidenceLevel",
  "defaultAgenticSettings/costWeight": "costWeight",
  "defaultAgenticSettings/maxRetries": "maxRetries",
  "defaultAgenticSettings/preconditions": "preconditions",
  "defaultAgenticSettings/timeoutSeconds": "timeoutSeconds",
  method: "method",
  name: "name",
  requestPayload: "payload",
  url: "url",
};

// Typed field name constants for better maintainability
const FORM_FIELDS = {
  description: "description" as const,
  inputPlaceholders: "inputPlaceholders" as const,
  jsonResponse: "jsonResponse" as const,
  method: "method" as const,
  name: "name" as const,
  payload: "payload" as const,
  url: "url" as const,
} as const;

const QueryActionForm = ({
  integration,
  queryAction,
}: QueryActionFormProps): null | ReactElement => {
  const isEdit = queryAction != null;

  const methods = useForm<QueryActionFormInputs>({
    defaultValues: {
      aiEnabled: false,
      confidenceLevel: "medium",
      costWeight: 3,
      jsonResponse: defaultEntitySchema,
      maxRetries: 3,
      method: "GET",
      timeoutSeconds: 3,
      type: "enrichment",
      url: "",
    },
  });

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = methods;

  useEffect(() => {
    if (queryAction) {
      reset({
        aiEnabled: queryAction.defaultAgenticSettings != null,
        confidenceLevel:
          queryAction.defaultAgenticSettings?.confidenceLevel || "low",
        costWeight: queryAction.defaultAgenticSettings?.costWeight || 1,
        description: queryAction.description || "",
        entityMappings:
          queryAction?.outputMappings?.map((m) => ({
            entityQualifiedName: m.qualifiedName,
            isPersisted: true,
            isRoot: m.isRoot ?? false,
            liquidTemplate: m.liquidTemplate,
            outputIsJson: m.outputType === "LIST",
            relationship: m.relationship,
            value: "",
          })) ?? [],
        httpParameters: queryAction?.apiParameters ?? [],
        inputEntityQualifiedName:
          queryAction?.inputMappings?.[0]?.qualifiedName ?? "",
        jsonResponse:
          integration.type === IntegrationType.LocalTransform &&
          queryAction?.requestPayload
            ? queryAction.requestPayload
            : defaultEntitySchema,
        maxRetries: queryAction.defaultAgenticSettings?.maxRetries || 3,
        method: queryAction?.method || "GET",
        name: queryAction?.name || "",
        payload: queryAction?.requestPayload ?? "",
        preconditions: queryAction.defaultAgenticSettings?.preconditions || "",
        timeoutSeconds: queryAction.defaultAgenticSettings?.timeoutSeconds ?? 5,
        type: queryAction?.type || "enrichment",
        url: queryAction?.url || "",
      });
    }
  }, [integration, queryAction, reset]);

  const [apiIsloading, setApiIsloading] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    isError: false,
    message: "OK",
    status: 200,
  });

  const [selectedTab, setSelectedTab] = useState("Headers");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReplacementDialogOpen, setIsReplacementDialogOpen] = useState(false);

  const { clearServerErrors, handleServerError, serverErrors } =
    useServerErrors({
      fieldNameMapping,
      formMethods: methods,
    });

  const partialValidateBeforeSend = async (): Promise<void> => {
    methods.clearErrors([FORM_FIELDS.name, FORM_FIELDS.description]);
    setSelectedTab("Payloads");
    await new Promise((resolve) => setTimeout(resolve, 0));
    const isValid = await methods.trigger([
      FORM_FIELDS.url,
      FORM_FIELDS.payload,
      FORM_FIELDS.method,
    ]);
    if (isValid) {
      handleSendRequest(methods.getValues());
    }
  };

  const handleSendRequest = (data: QueryActionFormInputs): void => {
    try {
      const fieldsWithPlaceholders =
        data.payload +
        "\n" +
        data.url +
        "\n" +
        data.httpParameters.map((p) => p.name + "=" + p.value).join("\n");
      const matches = Array.from(
        fieldsWithPlaceholders.matchAll(entityPlaceholderRegex),
      );
      const entityMatches = matches.map((m) => m[1]);

      const uniquePlaceholders = [...new Set([...entityMatches])];

      const existingPlaceholders =
        getValues(FORM_FIELDS.inputPlaceholders) || [];

      const mergedPlaceholders = uniquePlaceholders.map((placeholder) => {
        const existing = existingPlaceholders.find(
          (p) => p.pattern === placeholder,
        );
        return {
          pattern: placeholder,
          value: existing ? existing.value : "",
        };
      });

      if (mergedPlaceholders.length > 0) {
        setValue(FORM_FIELDS.inputPlaceholders, mergedPlaceholders);
        setIsReplacementDialogOpen(true);
      } else {
        sendRequest(data);
      }
    } catch (error) {
      console.error("Error in send:", error);
    }
  };

  const sendRequest = async (data: QueryActionFormInputs): Promise<void> => {
    setApiIsloading(true);
    try {
      const body = {
        apiParameters: data.httpParameters,
        description: data.description,
        id: "",
        inputMappings:
          data.inputPlaceholders?.map((m) => ({
            qualifiedName: m.pattern,
            value: m.value,
          })) ?? [],
        integrationId: integration.id,
        method: data.method,
        name: data.name,
        outputMappings: [],
        requestPayload: data.payload,
        type: data.type,
        url: data.url,
      };

      const response = await testQueryAction({
        body: body,
      });

      if (response.data == null) {
        throw new Error("Failed to send request");
      }

      setApiStatus({
        isError: response.data.isError,
        message: response.data.message,
        status: response.data.statusCode,
      });

      setValue(
        FORM_FIELDS.jsonResponse,
        wrapJsonLists(response.data.rawResponse),
      );
    } catch (error) {
      console.error("Error making API request:", error);
      setApiStatus({ isError: true, message: "Request failed", status: 500 });
      setValue(
        FORM_FIELDS.jsonResponse,
        JSON.stringify({ error: "Request failed" }, null, 2),
      );
    } finally {
      setApiIsloading(false);
      setSelectedTab("Entity Mapping");
    }
  };

  const onApiSubmit = async (data: QueryActionFormInputs): Promise<void> => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    clearServerErrors();

    // Only require Name and Description on Save
    const currentName = methods.getValues(FORM_FIELDS.name);
    const currentDescription = methods.getValues(FORM_FIELDS.description);

    const hasClientValidationError = validateRequiredFields(
      [
        {
          errorMessage: "Please enter a name",
          fieldName: FORM_FIELDS.name,
          value: currentName,
        },
        {
          errorMessage: "Please enter a brief description",
          fieldName: FORM_FIELDS.description,
          value: currentDescription,
        },
      ],
      methods.setError as UseFormSetError<Record<string, unknown>>,
    );

    if (hasClientValidationError) {
      setIsSubmitting(false);
      return;
    }

    try {
      const body = {
        apiParameters: data.httpParameters,
        defaultAgenticSettings: data.aiEnabled
          ? {
              confidenceLevel: data.confidenceLevel,
              costWeight: data.costWeight,
              maxRetries: data.maxRetries,
              preconditions: data.preconditions,
              timeoutSeconds: data.timeoutSeconds,
            }
          : undefined,
        description: data.description,
        id: "",
        inputEntities:
          integration.type === IntegrationType.LocalTransform
            ? [{ qualifiedName: data.inputEntityQualifiedName }]
            : undefined,
        integrationId: integration.id,
        method: data.method,
        name: data.name,
        outputMappings: data.entityMappings?.map((m) => ({
          isRoot: m.isRoot || false,
          liquidTemplate: m.liquidTemplate ?? "",
          outputType: (m.outputIsJson ? "LIST" : "STRING") as "LIST" | "STRING",
          qualifiedName: m.entityQualifiedName,
          relationship: m.relationship,
        })),
        requestPayload:
          integration.type === IntegrationType.LocalTransform
            ? data.jsonResponse
            : data.payload,
        type: data.type,
        url: data.url,
      };

      if (queryAction) {
        await updateQueryAction({
          body: body,
          path: {
            queryActionId: queryAction.id,
          },
        });
      } else {
        await createQueryAction({
          body: body,
        });
      }

      toast.success(
        `Query action ${isEdit ? "updated" : "created"} successfully`,
      );

      openPage($router, "integrations");
    } catch (error) {
      console.error("Query action submission error:", error);
      handleServerError(error);
      toast.error(`Failed to ${isEdit ? "update" : "create"} query action`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<QueryActionFormInputs> = (data) =>
    onApiSubmit(data);

  const aiEnabled = watch("aiEnabled");

  return (
    <>
      <BreadCrumbs
        pages={[
          {
            href: getPagePath($router, "integrationsEdit", {
              integrationId: integration.id,
            }),
            name: integration.name,
          },
        ]}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-10 grid grid-cols-8 gap-x-6 gap-y-8 sm:grid-cols-8">
            <div className="sm:col-span-7">
              <InputFieldGroup<QueryActionFormInputs>
                errors={errors}
                fieldName="name"
                label="Name"
                placeHolder="Query Action Name"
                register={register}
              />
            </div>
            <div className="sm:col-span-1">
              <SelectFieldGroup<QueryActionFormInputs>
                errors={errors}
                fieldName="type"
                label="Type"
                options={[
                  { label: "Enrichment", value: "enrichment" },
                  { label: "Poller", value: "poller" },
                ]}
                register={register}
              />
            </div>
            <div className="sm:col-span-full">
              <InputFieldGroup<QueryActionFormInputs>
                errors={errors}
                fieldName="description"
                label="Description"
                multiline={true}
                placeHolder="Describe what this query action does so that AI has better context"
                register={register}
              />
            </div>
            {(integration.type === IntegrationType.Custom ||
              integration.type === IntegrationType.Google) && (
              <>
                <div className="sm:col-start- min-w-0 overflow-hidden sm:col-span-1">
                  <SelectFieldGroup<QueryActionFormInputs>
                    errors={errors}
                    fieldName="method"
                    label="Method"
                    options={[
                      { color: "#10b981", label: "GET", value: "GET" },
                      { color: "#f59e0b", label: "POST", value: "POST" },
                      { color: "#8b5cf6", label: "PUT", value: "PUT" },
                      { color: "#ef4444", label: "DELETE", value: "DELETE" },
                    ]}
                    register={register}
                    rules={{ required: "Enter a method" }}
                  />
                </div>

                <QueryActionURLField baseUrl={integration.baseUrl} />

                <div className="min-w-0 overflow-hidden pt-8 sm:col-span-1">
                  <Button
                    className="w-full"
                    color="emerald"
                    onClick={partialValidateBeforeSend}
                    type="button"
                  >
                    Send
                  </Button>
                </div>
              </>
            )}
            {integration.type === IntegrationType.LocalTransform && (
              <div className="sm:col-span-full">
                <div className="flex flex-col gap-2">
                  <EntitySelectField
                    control={control}
                    errors={errors}
                    fieldName="inputEntityQualifiedName"
                    label="Input Entity"
                    placeHolder="Enter entity"
                    rules={{
                      required: "Input entity type is required",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="mt-10">
            {(integration.type === IntegrationType.Custom ||
              integration.type === IntegrationType.Google) && (
              <>
                <TabView onTabChange={setSelectedTab} selectedTab={selectedTab}>
                  <TabPanel header="Headers">
                    <APIParameterInlineTable
                      currentParameterType={ApiParameterType.HEADER}
                      showDescription={true}
                    />
                  </TabPanel>
                  <TabPanel header="Query Parameter">
                    <APIParameterInlineTable
                      currentParameterType={ApiParameterType.QUERY_PARAMETER}
                      showDescription={false}
                    />
                  </TabPanel>
                  <TabPanel header="Payloads">
                    <QueryActionPayloadField editorFormat="json" />
                  </TabPanel>
                  <TabPanel header="Entity Mapping" isLoading={apiIsloading}>
                    <MultiEntityMappingTable />
                    <div className="h-96 rounded-md bg-zinc-900">
                      <PanelGroup className="space-x-2" direction="horizontal">
                        <Panel
                          className="card_icon rounded-lg p-2 shadow-sm"
                          defaultSize={50}
                          minSize={20}
                        >
                          <div className="flex w-full items-center justify-between">
                            <span className="text-sm text-zinc-500">
                              Path Explorer
                            </span>
                            <Badge
                              color={apiStatus.isError ? "red" : "emerald"}
                            >
                              {apiStatus.status} {apiStatus.message}
                            </Badge>
                          </div>
                          <div className="-mx-2 mt-2 border-t border-zinc-500" />
                          <Controller
                            control={control}
                            name="jsonResponse"
                            render={({ field: { onChange, value } }) => (
                              <MonacoJsonEditor
                                bordered={false}
                                loading={apiIsloading}
                                onChange={onChange}
                                value={value}
                              />
                            )}
                          />
                        </Panel>
                      </PanelGroup>
                    </div>
                  </TabPanel>
                  <TabPanel
                    header="AI Settings"
                    icon={
                      <div
                        className={`h-3 w-3 rounded-full ${
                          aiEnabled
                            ? "bg-green-500 shadow-lg shadow-green-500/50"
                            : "bg-red-500 shadow-lg shadow-red-500/50"
                        }`}
                      />
                    }
                  >
                    <CardLayout
                      header={
                        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
                          <div className="ml-4 mt-2">AI Configuration</div>
                          <div className="ml-4 mt-2 shrink-0">
                            <SwitchField>
                              <Label size="sm">AI Enabled</Label>
                              <Controller
                                control={control}
                                defaultValue={true}
                                name="aiEnabled"
                                render={({ field: { onChange, value } }) => (
                                  <Switch checked={value} onChange={onChange} />
                                )}
                              />
                            </SwitchField>
                          </div>
                        </div>
                      }
                    >
                      <QueryActionAISettings />
                    </CardLayout>
                  </TabPanel>
                </TabView>
              </>
            )}
            {integration.type === IntegrationType.LocalTransform && (
              <>
                <SingleEntityMappingTable />
                <Field>
                  <div className="px-2 py-2 sm:p-1 dark:text-gray-500">
                    <Label>Input Entity Schema</Label>
                    <Controller
                      control={control}
                      name="jsonResponse"
                      render={({ field: { onChange, value } }) => (
                        <MonacoJsonEditor
                          bordered={true}
                          className="mb-2 h-[300px]"
                          loading={apiIsloading}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                      rules={{
                        required: "Entity Schema is required",
                        validate: (value) => {
                          if (value) {
                            try {
                              const parsed = JSON.parse(value);
                              const valid = validateEntitySchema(parsed);
                              if (!valid) {
                                return "JSON is not a valid entity schema";
                              }
                              return true;
                            } catch (error) {
                              return `Invalid JSON: ${error}`;
                            }
                          } else {
                            return true;
                          }
                        },
                      }}
                    />
                  </div>
                  {errors?.jsonResponse && (
                    <ErrorMessage>{errors.jsonResponse.message}</ErrorMessage>
                  )}
                </Field>
              </>
            )}
            <div className="mt-6">
              <FormErrorList errors={errors} serverErrors={serverErrors} />
            </div>
            <div className="sticky bottom-0 z-10 mt-8 flex items-center justify-end px-4 py-3 shadow-inner">
              <Button color="emerald" disabled={isSubmitting} type="submit">
                {isSubmitting ? "Saving..." : "Save Query Action"}
              </Button>
            </div>
            {/* add for extra space for popups */}
            <div className="h-96" />
          </div>
          <PlaceholderDialog
            isOpen={isReplacementDialogOpen}
            onClose={() => setIsReplacementDialogOpen(false)}
            onSkip={() => {
              setIsReplacementDialogOpen(false);
              setSelectedTab("Entity Mapping");
            }}
            onSubmit={() => {
              setIsReplacementDialogOpen(false);
              sendRequest(methods.getValues());
            }}
          />
        </form>
      </FormProvider>
    </>
  );
};

export default QueryActionForm;
